import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiWifi, FiWifiOff, FiRefreshCw } = FiIcons;

const OfflineIndicator = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showOfflineMessage, setShowOfflineMessage] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setShowOfflineMessage(false);
    };

    const handleOffline = () => {
      setIsOnline(false);
      setShowOfflineMessage(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const handleRetry = () => {
    // Simulate reconnection attempt
    if (navigator.onLine) {
      setIsOnline(true);
      setShowOfflineMessage(false);
    }
  };

  return (
    <>
      {/* Connection Status Indicator */}
      <div className="fixed top-20 right-4 z-40">
        <AnimatePresence>
          {!isOnline && (
            <motion.div
              initial={{ opacity: 0, x: 100 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 100 }}
              className="bg-red-500 text-white px-4 py-2 rounded-lg shadow-lg flex items-center space-x-2"
            >
              <SafeIcon icon={FiWifiOff} />
              <span className="text-sm font-medium">Offline</span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Offline Message Banner */}
      <AnimatePresence>
        {showOfflineMessage && (
          <motion.div
            initial={{ opacity: 0, y: -100 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -100 }}
            className="fixed top-20 left-4 right-4 z-40 md:left-auto md:right-4 md:w-96"
          >
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 shadow-lg">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <SafeIcon icon={FiWifiOff} className="text-yellow-600 text-lg" />
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-yellow-800">
                    You're offline
                  </h4>
                  <p className="text-sm text-yellow-700 mt-1">
                    Some features may not be available. You can still browse cached content.
                  </p>
                  <div className="flex items-center space-x-2 mt-3">
                    <motion.button
                      onClick={handleRetry}
                      className="flex items-center space-x-1 px-3 py-1 bg-yellow-600 text-white text-sm rounded hover:bg-yellow-700 transition-colors"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <SafeIcon icon={FiRefreshCw} className="text-xs" />
                      <span>Retry</span>
                    </motion.button>
                    <button
                      onClick={() => setShowOfflineMessage(false)}
                      className="text-sm text-yellow-600 hover:text-yellow-700"
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Back Online Notification */}
      <AnimatePresence>
        {isOnline && showOfflineMessage === false && (
          <motion.div
            initial={{ opacity: 0, y: -100 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -100 }}
            transition={{ delay: 0.5 }}
            className="fixed top-20 left-4 right-4 z-40 md:left-auto md:right-4 md:w-96"
          >
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 shadow-lg">
              <div className="flex items-center space-x-3">
                <SafeIcon icon={FiWifi} className="text-green-600 text-lg" />
                <div>
                  <h4 className="text-sm font-medium text-green-800">
                    Back online
                  </h4>
                  <p className="text-sm text-green-700">
                    All features are now available.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default OfflineIndicator;